package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.StaffRecord
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.staff.GetStaffDetailsUseCase
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

/** What one workplace keeps about me (SH-47): read-only, my own only. */
data class MyStaffDetails(
    val workplaceId: String,
    val workplaceName: String,
    val engagement: Engagement,
    val record: StaffRecord?,
    val readiness: StaffReadiness?,
    val notes: String?
) {
    /** Anything worth showing: dates, notes, or a supervision mark. */
    val hasAnything: Boolean
        get() = record != null || !notes.isNullOrBlank() || engagement.competence == Competence.NEEDS_SUPERVISION
}

/**
 * Profile: the person's own competence mark, medical exam and OHS dates and
 * availability notes at each of their workplaces (the employee sees their own, never others').
 */
class MyStaffDetailsViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getStaffDetails: GetStaffDetailsUseCase
) : ViewModel() {
    private val _state = MutableStateFlow<List<MyStaffDetails>>(emptyList())
    val state: StateFlow<List<MyStaffDetails>> = _state.asStateFlow()
    private val job = JobSlot()

    init {
        job.launch(viewModelScope) {
            getCurrentUserUseCase()
                .map { user -> user?.let { it.uid to it.engagements.values.filter { e -> e.isActive } } }
                .distinctUntilChanged()
                .collect { me ->
                    if (me == null) {
                        _state.value = emptyList()
                        return@collect
                    }
                    val (uid, engagements) = me
                    val found = coroutineScope {
                        engagements.map { engagement ->
                            async {
                                val details = getStaffDetails(uid, engagement.workplaceId, includeDates = true, includeNotes = true).getOrNull()
                                MyStaffDetails(
                                    workplaceId = engagement.workplaceId,
                                    workplaceName = engagement.cachedWorkplaceName.orEmpty(),
                                    engagement = engagement,
                                    record = details?.record,
                                    readiness = details?.readiness?.takeIf { details.record != null },
                                    notes = details?.notes
                                )
                            }
                        }.awaitAll()
                    }
                    _state.update { found.filter { it.hasAnything }.sortedBy { it.workplaceName } }
                }
        }
    }
}
